import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import clsx from 'clsx';
import {nanoid} from 'nanoid';

import {i18n} from '@netx/core/i18n';
import {Button, type DialogContentProps} from '@mui/material';
import useMediaQuery from '../hooks/useMediaQuery';
import Dialog, {DialogProps} from '../Dialog';
import type {FormActions, FormChanges, FormType, FormValues} from '../Form';
import LegacyForm from '../Form/LegacyForm';
import SubmitButton from '../SubmitButton';
import {
	composeClasses,
	styled,
	useTheme,
	type WithClasses,
	type WithMuiPropOverrides,
} from '../Theme';

type OverridenDialogContentProps = WithMuiPropOverrides<DialogContentProps>;

import {
	legacyFormDialogClasses,
	getLegacyFormDialogUtilityClass,
	LegacyFormDialogClasses,
	LEGACY_FORM_DIALOG_CLASS_NAME,
} from './legacyFormDialogClasses';

type LegacyFormDialogUtiltiyClassProps = WithClasses<
	LegacyFormDialogClasses,
	{
		disabled?: boolean;
	}
>;

const useUtilityClasses = (ownerState: LegacyFormDialogUtiltiyClassProps) => {
	const {classes, disabled} = ownerState;
	const slots = {
		root: ['root', disabled && 'disabled'],
		actions: ['actions'],
		cancelButton: ['cancelButton'],
		content: ['content'],
		form: ['form'],
		progress: ['progress'],
		submitButton: ['submitButton'],
	};
	return composeClasses(slots, getLegacyFormDialogUtilityClass, classes);
};

const LegacyFormDialogRoot = styled(Dialog, {
	name: LEGACY_FORM_DIALOG_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => {
	const {disabled} = ownerState as LegacyFormDialogUtiltiyClassProps;
	return {
		...(disabled
			? {
					[`& .${legacyFormDialogClasses.form}`]: {
						...theme.mixins.disabled,
						pointerEvents: 'none !important',
					},
			  }
			: null),
	};
});

const LegacyFormDialogForm = styled(LegacyForm, {
	name: LEGACY_FORM_DIALOG_CLASS_NAME,
	slot: 'Form',
})(({theme}) => ({
	display: 'flex',
	flexDirection: 'column',
	flex: '1 1 auto',
	overflow: 'hidden',
	padding: theme.spacing(1, 0),
}));

const LegacyFormDialogCancelButton = styled(Button, {
	name: LEGACY_FORM_DIALOG_CLASS_NAME,
	slot: 'CancelButton',
})(({theme}) => ({
	// marginRight: theme.spacing(1),
	[`& + .${legacyFormDialogClasses.submitButton}`]: {
		marginLeft: theme.spacing(1),
	},
}));

const LegacyFormDialogSubmitButton = styled(SubmitButton, {
	name: LEGACY_FORM_DIALOG_CLASS_NAME,
	slot: 'SubmitButton',
})(({theme}) => ({}));

export type LegacyFormDialogProps<
	WithForm extends boolean = false,
	TValues extends FormValues = FormValues,
> = Omit<DialogProps, 'classes' | 'onSubmit' | 'open'> &
	WithClasses<
		LegacyFormDialogClasses,
		{
			form?: FormType<TValues>;
			formId?: string;
			cancelLabel?: string;
			confirmLabel?: string;
			hasChanged?: boolean;
			invalid?: boolean;
			isValid?: boolean;
			open?: boolean;
			progress?: null | number;
			requiresChanges?: boolean;
			submitting?: boolean;
			DialogContentProps?: Partial<
				Omit<OverridenDialogContentProps, 'className'>
			>;
		}
	> &
	(WithForm extends true
		? {
				form: FormType<TValues>;
				onSubmit?: (
					values: TValues,
					changes: FormChanges<TValues>,
					actions: FormActions<TValues>,
				) => Promise<any>;
		  }
		: {
				form?: undefined;
				onSubmit: (event: React.FormEvent) => Promise<any>;
		  });

/**
 * Standard dialog for forms
 */
export const LegacyFormDialogPrimitive = <
	WithForm extends boolean = false,
	TValues extends FormValues = FormValues,
>({
	children,
	classes: _classes,
	className,

	cancelLabel,
	disabled,
	confirmLabel,
	disableEscapeKeyDown,
	fullScreenWhenMobile = true,
	fullScreen,
	fullSize,
	form,
	formId: _formId,
	hasChanged,
	invalid,
	isValid,
	onClose,
	onSubmit,
	progress,
	requiresChanges = true,
	submitting,

	...props
}: LegacyFormDialogProps<WithForm, TValues>) => {
	const formId = useMemo(() => _formId || `form-${nanoid()}`, [_formId]);

	cancelLabel = cancelLabel != null ? cancelLabel : i18n.t('i18n.cancel');
	confirmLabel = confirmLabel != null ? confirmLabel : i18n.t('i18n.submit');

	const isUncontrolled = useRef(props.open == null);
	const open = props.open == null ? true : props.open === true;

	useEffect(() => {
		open != null && setIsOpen(open);
		if (isUncontrolled.current && open != null) {
			console.warn('switching from uncontrolled to controlled');
			// isUncontrolled.current = false;
		}
	}, [props.open]);

	const theme = useTheme();

	const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

	const [isOpen, setIsOpen] = useState(open);

	const changed = form?.hasChanged || hasChanged;
	const valid = isValid !== false && form?.isValid !== false;
	const isSubmitting = form?.isSubmitting || submitting;

	const ownerState: LegacyFormDialogUtiltiyClassProps = {
		classes: _classes,
		disabled: disabled || isSubmitting,
	};

	const classes = useUtilityClasses(ownerState);

	const submitEnabled =
		!isSubmitting && invalid !== true && valid && (!requiresChanges || changed);

	const handleClose = useCallback(
		(event: any, reason: any) => {
			if (!isSubmitting && reason !== 'backdropClick') {
				isUncontrolled.current && setIsOpen(false);
				onClose?.(event, reason);
			}
		},
		[onClose, isSubmitting],
	);

	const handleCancel = useCallback(
		(event: any) => {
			isUncontrolled.current && setIsOpen(false);
			onClose?.(event, 'escapeKeyDown');
		},
		[onClose],
	);

	return (
		<LegacyFormDialogRoot
			fullWidth
			maxWidth="sm"
			fullScreenWhenMobile={fullScreenWhenMobile}
			fullScreen={fullScreen}
			fullSize={fullSize}
			error={form?.submissionError}
			isWorking={isSubmitting || progress != null}
			dialogActions={
				<>
					{!smallScreen ? (
						<LegacyFormDialogCancelButton
							className={classes.cancelButton}
							color="primary"
							data-testid="form-cancel"
							onClick={handleCancel}
						>
							{cancelLabel}
						</LegacyFormDialogCancelButton>
					) : null}
					<LegacyFormDialogSubmitButton
						form={formId}
						className={classes.submitButton}
						fullWidth={smallScreen}
						data-testid="form-submit"
						disabled={!submitEnabled}
						label={confirmLabel}
						size={smallScreen ? 'large' : undefined}
					/>
				</>
			}
			{...props}
			className={clsx(classes.root, className)}
			disableEscapeKeyDown={disableEscapeKeyDown || isSubmitting}
			disabled={disabled || isSubmitting}
			open={isOpen}
			ownerState={ownerState}
			progress={progress}
			onClose={handleClose}
		>
			<LegacyFormDialogForm
				id={formId}
				className={classes.form}
				form={form}
				// TODO: fix
				onSubmit={onSubmit as any}
			>
				{children}
			</LegacyFormDialogForm>
		</LegacyFormDialogRoot>
	);
};

export const LegacyFormDialog = LegacyFormDialogPrimitive;

export default LegacyFormDialog;
